import { Component, defineElement } from '@theme/component';

/** Contact form runtime. */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Never transmitted: `website` is the honeypot and `jsCheck` proves the script
// ran. Both are client-side gates only.
const UNTRANSMITTED_FIELDS = new Set(['website', 'jsCheck']);

const CONTROL_SELECTOR = 'input, textarea, select';

/**
 * Theme 1.0 parity: a US number reduced to its bare ten digits, or an empty string when the input cannot be one.
 */
export function normalizeUsPhone(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? digits : '';
}

/** Shopify order names on this store: digits with an optional leading `#`. */
export function isOrderNumber(value) {
  return /^#?\s*\d+$/.test(String(value || '').trim());
}

/** The listed values a conditional field is shown for. */
export function parseConditionValues(raw) {
  return String(raw || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

class ContactForm extends Component {
  ready() {
    this.form = this.querySelector('[data-contact-region="form"]');
    this.successRegion = this.querySelector('[data-contact-region="success"]');
    this.summary = this.querySelector('[data-error-summary]');
    this.submitButton = this.querySelector('[data-contact-submit]');
    this.jsCheck = this.querySelector('input[name="jsCheck"]');
    this.honeypot = this.querySelector('input[name="website"]');
    this.submitting = false;
    this.editorPreview = false;

    if (!this.form) return;

    if (this.jsCheck) this.jsCheck.value = 'enabled';
    if (this.submitButton) this.submitButton.disabled = false;
    for (const trigger of this.querySelectorAll('[data-file-trigger]')) trigger.disabled = false;

    this.syncConditionalFields();

    this.listen(this, 'change', this.handleChange);
    this.listen(this, 'click', this.handleClick);
    this.listen(this, 'submit', this.handleSubmit);
  }

  /* ---------------------------------------------------------------- helpers */

  controlIn(field) {
    return field?.querySelector(CONTROL_SELECTOR) || null;
  }

  message(key) {
    return this.dataset[key] || '';
  }

  showSummary(text) {
    if (!this.summary) return;
    this.summary.textContent = text;
  }

  clearErrors() {
    this.showSummary('');
    for (const control of this.form.querySelectorAll('[aria-invalid="true"]')) {
      control.removeAttribute('aria-invalid');
      if (control.getAttribute('aria-describedby') === this.summary?.id) {
        control.removeAttribute('aria-describedby');
      }
    }
  }

  markInvalid(control) {
    control.setAttribute('aria-invalid', 'true');
    if (this.summary?.id) control.setAttribute('aria-describedby', this.summary.id);
  }

  /* ----------------------------------------------------- conditional fields */

  /**
   * A conditional field is hidden by Liquid and revealed here whenever the dropdown it names holds one of its listed values.
   */
  syncConditionalFields() {
    for (const field of this.querySelectorAll('[data-condition-name]')) {
      const owner = this.querySelector(
        `[data-form-select][data-select-name="${CSS.escape(field.dataset.conditionName)}"]`
      );
      const control = this.controlIn(owner);
      const values = parseConditionValues(field.dataset.conditionValues);
      field.hidden = !control || !values.includes(control.value);
    }
  }

  /* ------------------------------------------------------------ file picker */

  fileState(root) {
    return {
      input: root.querySelector('[data-file-input]'),
      trigger: root.querySelector('[data-file-trigger]'),
      label: root.querySelector('[data-file-trigger-label]'),
      name: root.querySelector('[data-file-name]'),
    };
  }

  renderFileState(root) {
    const { input, trigger, label, name } = this.fileState(root);
    if (!input || !trigger || !label || !name) return;

    const file = input.files?.[0];
    name.textContent = file ? file.name : '';
    label.textContent = file
      ? trigger.dataset.labelRemove || label.textContent
      : trigger.dataset.labelChoose || label.textContent;
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-file-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const root = trigger.closest('[data-form-file]');
    const { input } = this.fileState(root);
    if (!input) return;

    if (input.files?.length) {
      input.value = '';
      this.renderFileState(root);
      trigger.focus();
      return;
    }

    input.click();
  }

  handleChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-file-input]')) {
      this.renderFileState(target.closest('[data-form-file]'));
      return;
    }

    if (target.closest('[data-form-select]')) this.syncConditionalFields();
  }

  /* -------------------------------------------------------------- validation */

  /**
   * @returns {{missing: true}|{message: string}|null} why a visible field fails, or null
   */
  problemWith(field, control) {
    const value = control.type === 'checkbox' ? '' : control.value.trim();
    const empty = control.type === 'checkbox' ? !control.checked : !value;

    if (empty) return field.dataset.fieldRequired === 'true' ? { missing: true } : null;

    if (field.dataset.fieldType === 'email' && !EMAIL_PATTERN.test(value)) {
      return { message: this.message('errorEmail') };
    }

    if (field.dataset.fieldType === 'tel') {
      const digits = normalizeUsPhone(value);
      if (!digits) return { message: this.message('errorPhone') };
      // Theme 1.0 delivers the bare ten digits, so normalize in place.
      control.value = digits;
    }

    if (field.dataset.fieldFormat === 'order-number' && !isOrderNumber(value)) {
      return { message: this.message('errorOrderNumber') };
    }

    return null;
  }

  /**
   * Checks every visible field at once so the customer sees each problem in one pass.
   * @returns {{message: string, focus: Element}|null} the combined failure, or null
   */
  validate() {
    const failures = [];

    for (const field of this.form.querySelectorAll('[data-form-field]:not([hidden])')) {
      const control = this.controlIn(field);
      if (!control) continue;
      const problem = this.problemWith(field, control);
      if (!problem) continue;
      this.markInvalid(control);
      failures.push({
        ...problem,
        control,
        label: field.dataset.fieldLabel || '',
        required: field.dataset.fieldRequired === 'true',
      });
    }

    for (const input of this.form.querySelectorAll('[data-file-input]')) {
      const file = input.files?.[0];
      if (!file || file.size > 0) continue;
      failures.push({
        message: this.message('errorFile'),
        control: input.closest('[data-form-file]')?.querySelector('[data-file-trigger]'),
      });
    }

    if (!failures.length) return null;

    const anyMissing = failures.some((failure) => failure.missing);
    const lines = [];

    if (anyMissing) {
      const labels = failures.filter((failure) => failure.required).map((failure) => failure.label);
      lines.push(this.message('errorRequired') + labels.join(', '));
    }

    for (const failure of failures) {
      if (anyMissing && failure.required) continue;
      lines.push(failure.message);
    }

    return { message: lines.join('\n'), focus: failures[0].control };
  }

  /* -------------------------------------------------------------- submission */

  buildPayload() {
    const body = new FormData();

    for (const control of this.form.querySelectorAll('[name]')) {
      const name = control.getAttribute('name');
      if (!name || UNTRANSMITTED_FIELDS.has(name)) continue;
      if (control.closest('[hidden]')) continue;

      if (control.type === 'file') {
        const file = control.files?.[0];
        if (file) body.append(name, file, file.name);
        continue;
      }

      if (control.type === 'checkbox' && !control.checked) continue;

      body.append(name, control.value);
    }

    return body;
  }

  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    if (busy) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  /** The one place the two regions trade visibility. */
  swapToSuccess(show) {
    if (!this.form || !this.successRegion) return;
    this.form.hidden = show;
    this.successRegion.hidden = !show;
  }

  showSuccess() {
    if (!this.successRegion) return;
    this.swapToSuccess(true);
    this.successRegion.focus();
  }

  failWith(text) {
    this.showSummary(text);
    this.summary?.focus();
  }

  async handleSubmit(event) {
    if (event.target !== this.form) return;
    event.preventDefault();

    if (this.submitting) return;

    this.clearErrors();

    // Both spam gates fail closed and never reach the network.
    if (this.honeypot?.value.trim() || this.jsCheck?.value !== 'enabled') {
      this.failWith(this.message('errorGeneric'));
      return;
    }

    const failure = this.validate();
    if (failure) {
      this.showSummary(failure.message);
      failure.focus?.focus();
      return;
    }

    this.submitting = true;
    this.setBusy(true);

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        body: this.buildPayload(),
        headers: { Accept: 'application/json' },
        signal: this.abortController?.signal,
      });

      if (response.ok) {
        this.showSuccess();
        return;
      }

      this.failWith(await this.readError(response));
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.failWith(this.message('errorGeneric'));
    } finally {
      this.submitting = false;
      this.setBusy(false);
    }
  }

  /** Formspree replies with `{ errors: [{ message }] }`; anything else is generic. */
  async readError(response) {
    try {
      const payload = await response.json();
      const messages = (payload?.errors || []).map((entry) => entry?.message).filter(Boolean);
      if (messages.length) return messages.join(' ');
    } catch {
    }
    return this.message('errorGeneric');
  }

  /* ------------------------------------------------------------ theme editor */

  /** Design mode only; called by `assets/theme-editor.js`. */
  revealBlock(blockId, sourceElement) {
    const inSuccess =
      sourceElement instanceof Element && this.successRegion?.contains(sourceElement);

    if (!inSuccess) {
      this.releaseEditorSelection();
      return;
    }

    this.editorPreview = true;
    this.swapToSuccess(true);
  }

  /** Reverts a preview swap only. A real submission in the preview is never undone. */
  releaseEditorSelection() {
    if (!this.editorPreview) return;
    this.editorPreview = false;
    this.swapToSuccess(false);
  }
}

defineElement('dieux-contact-form', ContactForm);

export { ContactForm };
